import _ from "lodash"
import { Watch } from "vue-property-decorator"
import DistanceUnits from "./DistanceUnits"
import HomeMounted from "./HomeMounted"
import ProductService from "./ProductService"
import StorageHelper from "./StorageHelper"
import WaypointFeature from "./WaypointFeature"
import { CurrentTrack } from "./HomeData"
import { cleanFileName } from "./FileNameUtility"

export default class HomeWatches extends HomeMounted {
    @Watch('renderer')
    onChangeRenderer (val: string, oldVal: string) {
        const newRenderer = this.renderers.find(r => r.value === val) ?? null
        if (newRenderer) {
            if(newRenderer.value !== this.currentRenderer.GetType()) { //at first load, we want to be able to set the property without teardown/setup firing twice

                if(newRenderer.isSubscriberOnly && !this.$store.getters.isOrMightBeSubscribed) {
                    this.showSubscribeOnlyDialog(`${newRenderer.text} rendering is a subscriber only feature.`)
                    this.$nextTick(() => {
                        this.renderer = oldVal
                    })
                } else {
                    this.clearPermanentInteractions()
                    this.clearInteractions()
                    this.persistLayers()
                    this.clearLayers()
    
                    StorageHelper.setItem("Renderer", newRenderer.value)
    
                    this.currentRenderer.Dispose()
    
                    this.currentRenderer = newRenderer.creator(newRenderer.value)
                    this.setupRendererOnInitOrChange() // < initialization (e.g. interactions) in here
    
                    this.rebuildLayers()
                    this.setOperationForCurrentTrack()
                    this.closeSidebarIfMobile()
                }
            }
        } else {
            console.warn('renderer not found: ', val)
        }
    }

    @Watch('downloadOption')
    onDownloadOptionChanged(newVal: string) {
        if (newVal === CurrentTrack) {
            const currentTrack = this.getCurrentTrack();
            if(currentTrack) {
                this.saveFileName = cleanFileName(currentTrack.name);
            }
        } else {
            this.inferSuggestedFileName();
        }
    }

    @Watch('currentTrack')
    onCurrentTrackChanged(newVal: string) {
        this.setOperationForCurrentTrack()
        this.updateShowingTrackpoints()
        this.setTrackZIndexes()
    }

    @Watch('trackpointEditing')
    ontrackpointEditingChanged() {
        this.updateShowingTrackpoints()
    }

    @Watch('currentTrackHash')
    @Watch('distanceUnit')
    onElevationChartInvalidated(newVal: string) {
        if(this.elevationChart !== null) {
            this.showOrRedrawElevationChart()
        }
    }

    @Watch('operation')
    onOperationChanged (newVal: number) {
        if(newVal !== 0 && this.isFreeUserTryingToEditSharedTrack()) {
            this.$nextTick(() => this.operation = 0)            
        } else {
            this.setOperationForCurrentTrack()
            this.updateShowingTrackpoints()
        }
    }

    @Watch('$store.getters.showingPricingSidebar')
    async onPricingSidebarChanged(newVal: boolean) {
        if(newVal) {
            const productResponse = await ProductService.getFullAccessProduct();
            this.fullAccessProduct = productResponse.data
        }
    }


    @Watch('distanceUnit')
    onDistanceUnitChanged(newVal: string) {
        StorageHelper.setItem('DistanceUnit', newVal)
    }

    @Watch('sidebar')
    onSidebarChanged(newVal: boolean) {
        if(!newVal) {
            this.renamingTrack = false // don't persist renaming operation across sidebar being closed and reopened
        }

        StorageHelper.setItem('showSidebar', newVal ? 'true' : 'false')
    }

    @Watch('mergeSegmentsOnSaveToFile')
    onmergeSegmentsOnSaveToFileChanged(newVal: boolean) {
        StorageHelper.setItem('mergeSegmentsOnSaveToFile', newVal ? 'true' : 'false')
    }

    @Watch('cloudsidebar')
    public onCloudSidebarChanged(newVal: boolean) {
        if(newVal) {
            //showing the cloud side bar. get rid of the main sidebar, as it obstructs it
            //but remember whether we had it showing
            this.sidebarStateBeforeCloudSidebarWasShown = this.sidebar
            this.sidebar = false
        } else {
            // closing the cloud sidebar, restore the main sidebar to its previous state
            if(this.sidebarStateBeforeCloudSidebarWasShown !== null) {
                this.sidebar = this.sidebarStateBeforeCloudSidebarWasShown
                this.sidebarStateBeforeCloudSidebarWasShown = null
            }
        }
    }


    @Watch('selectedWaypoint')
    onSelectedWaypointChanged(newVal: WaypointFeature) {
        this.highlightSelectedWaypoint(newVal)
    }

    @Watch('tracks')
    onTracksChanged() {
        if(!this.settingTrackZIndexes) { // recursion guard (it sets tracks itself)
            this.settingTrackZIndexes = true
            try {
                this.setTrackZIndexes()
            }
            finally {
                this.settingTrackZIndexes = false
            }                
        }
    }

    @Watch('showingDistanceForCurrentTrack')
    onShowingDistanceForCurrentTrackChanged(newVal: boolean) {
        const currentTrack = this.getCurrentTrack()
        if(currentTrack) {
            if(newVal) {
                const distanceUnitData = DistanceUnits.find(d => d.value === this.distanceUnit)
                if(distanceUnitData) {
                    currentTrack.showDistanceMarkers(distanceUnitData)
                }
            } else {
                currentTrack.removeDistanceMarkersFromMap()
            }
        }
    }

    @Watch('showingDistanceForAllTracks')
    onShowingDistanceForAllTracksChanged(newVal: boolean) {
        
        if(newVal) {
            const distanceUnitData = DistanceUnits.find(d => d.value === this.distanceUnit)
            if(distanceUnitData) {
                this.tracks.forEach(t => t.showDistanceMarkers(distanceUnitData))
            }
        } else {
            this.tracks.forEach(t => t.removeDistanceMarkersFromMap())
        }
    }

    @Watch('dialog')
    onDialogChanged(newVal: string) {
        if(newVal === 'login') {
            this.gpxAsString = this.noContent() ? '' : JSON.stringify(this.getGpx()) // send the gpx as a string along with the login form, so they don't lose it
        }
    }

    @Watch('$store.getters.isOrMightBeSubscribed')
    onValidAllowedRenderersChanged(newVal: any) {
        // this is probably only going to kick in if their subscription has recently expired, and they have got a subscriber renderer in their cookie
        if(!_.some(newVal, r => r.value === this.renderer)) {
            const firstAllowedRenderer = _.first(this.validRenderers.filter(r => !r.isSubscriberOnly))
            if(firstAllowedRenderer) {
                this.renderer = firstAllowedRenderer.value
            }
        }
    }
}